/**
 * Where a popover stops being visible.
 *
 * An absolutely positioned panel is only as free as its nearest clipping ancestor lets it be, and on
 * this app that ancestor is often the player: `.player` sets `overflow: hidden` for its rounded corners
 * and its fullscreen box, so a menu that opens past its edge is simply cut off — with no scrollbar and
 * nothing on screen to say rows are missing. Two consumers measure against it: `Menu` caps a popover's
 * height to the room it actually has, and the failure box's jump-out flips to the other side.
 *
 * Not a hook and not a component: it reads layout, which is the view's job, and it is the same three
 * lines both callers were about to write.
 */

/**
 * The nearest ancestor whose overflow clips, or `null` when nothing does short of the viewport.
 *
 * `overflowX`/`overflowY` rather than the shorthand, because an element clips if **either** axis does
 * — a `overflow-x: hidden` wrapper cuts a popover's shadow just as effectively as a box that hides
 * both. The computed value is what matters here: `visible` is the only value that does not clip
 * (`clip`, `hidden`, `auto` and `scroll` all do, and `auto` is the sneaky one — it clips whenever it
 * is not scrolling).
 */
export function clippingAncestor(element: HTMLElement): HTMLElement | null {
  for (let node = element.parentElement; node !== null; node = node.parentElement) {
    const style = getComputedStyle(node);
    if (style.overflowY !== 'visible' || style.overflowX !== 'visible') return node;
  }
  return null;
}

/**
 * Breathing room between a popover and the edge it must not touch.
 *
 * Not a fit test: a panel that "fits" flush against a clipping edge has its shadow and its corner
 * rounding cut, which reads as clipped even when every row is visible — and the layouts this exists for
 * land within a pixel of exactly that.
 */
export const CLIP_MARGIN_PX = 16;

/**
 * The box a popover has to stay inside: its clipping ancestor's, or the viewport's when nothing clips
 * short of it. The second half is the common case on an ordinary page and the reason this exists —
 * `clippingAncestor` answering `null` used to mean "no measurement at all" at every call site.
 *
 * `documentElement.clientWidth`, not `window.innerWidth`: Gecko reserves a classic scrollbar gutter
 * that Blink's overlay scrollbar does not, so `innerWidth` is ~12 px past where a panel is actually
 * visible in Firefox ([browser-driving.md](../../../../docs/specs/browser-driving.md) measured the same
 * difference on the minimized bar). `clientHeight` for the same reason on the other axis.
 */
export function clipBox(element: HTMLElement): DOMRect {
  const root = element.ownerDocument.documentElement;
  const viewport = new DOMRect(0, 0, root.clientWidth, root.clientHeight);
  const clip = clippingAncestor(element);
  if (clip === null) return viewport;

  /*
   * The **intersection**, not the ancestor's own box: below `md` the player is full-bleed and reaches
   * a gutter's width past the page on each side, so a panel fitted to the player alone came to rest
   * 1 px from the window's left edge — inside the thing that clips it, and still against the glass.
   * Whichever edge is nearer is the one a popover has to respect.
   */
  const box = clip.getBoundingClientRect();
  const left = Math.max(box.left, viewport.left);
  const top = Math.max(box.top, viewport.top);
  return new DOMRect(
    left,
    top,
    Math.max(0, Math.min(box.right, viewport.right) - left),
    Math.max(0, Math.min(box.bottom, viewport.bottom) - top),
  );
}

/**
 * How far to nudge a laid-out popover along the inline axis so it clears **both** edges of its room,
 * {@link CLIP_MARGIN_PX} included — `0` when it already fits.
 *
 * A shift rather than a flip of the anchored edge, and that is the decision: `.popoverMatch` grows past
 * its trigger's right edge for a long option, so a panel can be wider than the control it hangs off and
 * an end-anchored copy of it would simply overflow the other side instead. Shifting by exactly the
 * overflow also needs to know nothing about which edge the stylesheet anchored, which is what makes it
 * work for the centred placements too — those are a `translateX(-50%)`, and there is no edge to flip.
 *
 * The start correction is applied *after* the end one, so a panel wider than the whole room ends up
 * pinned to the start edge — the side its first words are on.
 */
export function inlineNudge(panel: DOMRect, box: DOMRect): number {
  const pastEnd = panel.right - (box.right - CLIP_MARGIN_PX);
  const shifted = pastEnd > 0 ? -pastEnd : 0;
  const pastStart = box.left + CLIP_MARGIN_PX - (panel.left + shifted);
  return pastStart > 0 ? shifted + pastStart : shifted;
}

/**
 * How far a laid-out popover reaches past the clipping edge it opens towards, {@link CLIP_MARGIN_PX}
 * included — `0` when it fits.
 *
 * The panel's own box rather than its trigger's, deliberately: a popover sits a gap away from the
 * control that opened it, and measuring from the trigger makes every answer that gap too generous.
 * Shrinking a panel by exactly what it overflows needs no knowledge of the anchor, the gap or the
 * direction the stylesheet happens to anchor from.
 */
export function overflowPast(panel: DOMRect, clip: HTMLElement, direction: 'up' | 'down'): number {
  const box = clip.getBoundingClientRect();
  const past =
    direction === 'up'
      ? box.top + CLIP_MARGIN_PX - panel.top
      : panel.bottom - (box.bottom - CLIP_MARGIN_PX);
  return Math.max(0, past);
}
